import { BonusClassification } from '@synergy/models';
import { Box, Button, Chip, Stack, Tooltip } from '@mui/material';
import { EuroIcon, StarIcon } from '@synergy/theme';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import {
    PaloColumn,
    PaloFilterChips,
    PaloListView,
    PaloLockedSwitch,
    paloLockedHint,
    usePaloLockedRecords,
} from '@synergy/components-palo';
import { ReactElement, useEffect } from 'react';
import { getLocalizedString } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import urljoin from 'url-join';

import { PaloContactName } from '../../../../common/components/palo/palo-contact-card';
import { PaloFilterPanel } from '../../../../common/components/palo/palo-filter-panel';
import { PaloListLogo } from '../../../../common/components/palo/palo-list-logo';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { SupplierListItem } from '../supplier-list/models/supplier-list.item';
import { fetchSuppliers } from '../../../../common/actions/supplier-actions';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { usePaloColumnFilters } from '../../../../common/components/palo/use-palo-column-filters';
import { usePaloListSelection } from '../../../../common/components/palo/palo-list-selection';
import { usePaloListViews } from '../../../../common/components/palo/use-palo-list-views';
import { usePaloLockedCount } from '../../../../common/components/palo/use-palo-locked-count';
import { usePaloSort } from '../../../../common/components/palo/use-palo-sort';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const addressOf = (supplier: SupplierListItem) =>
    supplier.mainAddress || supplier.addresses?.find(address => address.isMainAddress) || supplier.addresses?.[0];

/**
 * What was agreed with this supplier. It is kept as a classification with a
 * validity, plus a flag for whether we settle for them — the overview says it
 * in one line instead of leaving it to the detail mask (#2193).
 */
/**
 * The euro mark stands for an agreement to charge through POOLALPIN, which only
 * contract suppliers have. The switch alone is also set on some 600 suppliers
 * without a contract, whose rare orders go through POOL as well (R-272).
 */
const chargesAsContractSupplier = (supplier: {
    classification?: BonusClassification | null;
    isChargingSupplier?: boolean;
}): boolean => BonusClassification.Contract === supplier.classification && true === supplier.isChargingSupplier;

const modelLabelId: Record<BonusClassification, string> = {
    [BonusClassification.Contract]: 'app.supplierModel.contract',
    [BonusClassification.Bonus]: 'app.supplierModel.bonus',
    [BonusClassification.BonusPlus]: 'app.supplierModel.bonusPlus',
};

/** How far ahead an ending agreement is flagged. */
const EXPIRING_WITHIN_DAYS = 90;

type ContractState = 'active' | 'expiring' | 'expired' | 'upcoming';

/**
 * Where the agreement with a supplier stands today. Only suppliers with an
 * arrangement have one; an open end counts as running.
 */
export const contractState = (
    supplier: Pick<SupplierListItem, 'classification' | 'classificationFrom' | 'classificationTo'>,
    now: Date = new Date()
): ContractState | undefined => {
    if (!supplier.classification) {
        return undefined;
    }

    const day = (value: string | Date): number => {
        const date = new Date(value);

        return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    };
    const today = day(now);

    if (supplier.classificationFrom && day(supplier.classificationFrom) > today) {
        return 'upcoming';
    }

    if (!supplier.classificationTo) {
        return 'active';
    }

    const end = day(supplier.classificationTo);

    if (end < today) {
        return 'expired';
    }

    return end - today <= EXPIRING_WITHIN_DAYS * 24 * 60 * 60 * 1000 ? 'expiring' : 'active';
};

const contractColor: Record<ContractState, 'success' | 'warning' | 'error' | 'info'> = {
    active: 'success',
    expiring: 'warning',
    expired: 'error',
    upcoming: 'info',
};

export const PaloSupplierList = (): ReactElement => {
    const { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns, savedFiltersMenu } =
        usePaloListViews('suppliers');
    const columnFilter = usePaloColumnFilters('suppliers');
    const intl = useIntl();
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { sort, params } = usePaloSort('name', 'asc');
    // Locked partners are out of the list unless they are asked for
    // (LST-S14); the switch that asks for them sits in the filter row.
    const locked = usePaloLockedRecords(params);
    const { flat: paginated, requesting } = useAppSelector(state => state.suppliers);
    const { apiBaseUrl, imageCachePaths } = useAppSelector(state => state.settings);
    const locales = useAppSelector(state => state.supportedLocales);
    // The same list the document filters offer under "Sachbearbeiter".
    const { sortedByName: staff } = useAppSelector(state => state.contacts);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);
    const imageBaseUrl = imageCachePaths?.['96x48'];
    const imageBase = apiBaseUrl && imageBaseUrl ? urljoin(apiBaseUrl, imageBaseUrl) : undefined;

    useEffect(() => {
        dispatch(fetchSuppliers(locked.params));
    }, [dispatch, locked.params]);

    const lockedMatches = usePaloLockedCount(
        '/suppliers',
        locked.params,
        !locked.includeLocked && !requesting && 0 === paginated.items.length
    );

    // Same bar as on members and persons (#2212).
    const { selection, actions: selectionActions } = usePaloListSelection(
        paginated.items,
        id =>
            dispatch((_d, _g, { api }) => api.contactsAndOrganizations.Supplier.SupplierDetail.lockSupplier(id)).then(
                result => result.success
            ),
        id =>
            dispatch((_d, _g, { api }) => api.contactsAndOrganizations.Supplier.SupplierDetail.unlockSupplier(id)).then(
                result => result.success
            ),
        () => dispatch(fetchSuppliers(locked.params))
    );

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const detailHref = (supplier: SupplierListItem): string => location.pathname + '/' + supplier.id;

    const columns: PaloColumn<SupplierListItem>[] = [
        {
            key: 'logo',
            type: 'icon',
            head: '',
            cell: supplier => (
                <PaloListLogo
                    name={supplier.name.trim()}
                    src={supplier.image && imageBase ? urljoin(imageBase, supplier.image) : undefined}
                    locked={supplier.isLocked}
                />
            ),
        },
        {
            key: 'number',
            link: true,
            type: 'documentNumber',
            sortKey: 'number',
            head: <FormattedMessage id="app.table.column.number" />,
            cell: supplier => supplier.number ?? supplier.id,
        },
        {
            key: 'type',
            head: <FormattedMessage id="app.table.column.type" />,
            // Two marks, two parameters, one field: picking one of them clears
            // the other, because a supplier that is both would otherwise be the
            // only one left after choosing either.
            quickFilter: {
                key: 'supplierType',
                options: [
                    { value: 'charging', label: <FormattedMessage id="app.label.contractChargingSupplier" /> },
                    { value: 'main', label: <FormattedMessage id="app.common.mainContact" /> },
                ],
                value:
                    'true' === searchParams.get('isChargingSupplier')
                        ? 'charging'
                        : 'true' === searchParams.get('isMain')
                        ? 'main'
                        : '',
                onChange: value => {
                    const next = new URLSearchParams(searchParams);

                    if ('true' === next.get('isChargingSupplier')) {
                        next.delete('classifications');
                    }

                    next.delete('isChargingSupplier');
                    next.delete('isMain');

                    // The same suppliers as the mark in the column.
                    if ('charging' === value) {
                        next.set('isChargingSupplier', 'true');
                        next.set('classifications', BonusClassification.Contract);
                    } else if ('main' === value) {
                        next.set('isMain', 'true');
                    }

                    next.set('page', '1');
                    setSearchParams(next);
                },
            },
            cell: supplier => (
                <Stack direction="row" gap={1} alignItems="center">
                    {chargesAsContractSupplier(supplier) && (
                        <Tooltip title={<FormattedMessage id="app.label.contractChargingSupplier" />}>
                            <Box sx={{ display: 'flex' }}>
                                <EuroIcon sx={{ fontSize: 18 }} />
                            </Box>
                        </Tooltip>
                    )}
                    {supplier.isMain && (
                        <Tooltip title={<FormattedMessage id="app.common.mainContact" />}>
                            <Box sx={{ display: 'flex' }}>
                                <StarIcon sx={{ fontSize: 18 }} />
                            </Box>
                        </Tooltip>
                    )}
                </Stack>
            ),
        },
        {
            key: 'name',
            type: 'name',
            sortKey: 'name',
            head: <FormattedMessage id="app.table.column.name" />,
            cell: supplier => <PaloContactName company={supplier} kind="supplier" />,
        },
        {
            // Only what the account actually records. A supplier is either
            // blocked or it is not; "Onboarding" and "in Verhandlung" have no
            // field behind them yet and are therefore not offered here.
            key: 'status',
            sortKey: 'status',
            type: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            // The one filter this list's endpoint already understands. The
            // others the briefing asks for — type, classification, country,
            // person in charge — have no parameter behind them yet (#2194).
            cell: supplier => (
                <Chip
                    size="small"
                    color={supplier.isLocked ? 'error' : 'success'}
                    label={<FormattedMessage id={supplier.isLocked ? 'app.status.locked' : 'app.status.active'} />}
                />
            ),
        },
        {
            key: 'model',
            sortKey: 'model',
            head: <FormattedMessage id="app.table.column.supplierModel" />,
            quickFilter: {
                key: 'classifications',
                options: Object.entries(modelLabelId).map(([value, labelId]) => ({
                    value,
                    label: <FormattedMessage id={labelId} />,
                })),
                value: searchParams.get('classifications') ?? '',
                onChange: value => {
                    const next = new URLSearchParams(searchParams);

                    if (value) {
                        next.set('classifications', value);
                    } else {
                        next.delete('classifications');
                    }

                    next.set('page', '1');
                    setSearchParams(next);
                },
            },
            cell: supplier =>
                supplier.classification ? (
                    <Stack direction="row" gap={0.5} alignItems="baseline">
                        <FormattedMessage id={modelLabelId[supplier.classification]} />
                        {chargesAsContractSupplier(supplier) && (
                            <Box component="span" sx={{ color: tokens.color.ink3, fontSize: 11 }}>
                                <FormattedMessage id="app.supplierModel.charging" />
                            </Box>
                        )}
                    </Stack>
                ) : null,
        },
        {
            // The supplier's categories. The classifications that stood here
            // are what inquiries get tagged with and are set on a single
            // supplier out of 3.433 — the column was empty on every row.
            key: 'productGroups',
            type: 'name',
            optional: true,
            wrap: true,
            head: <FormattedMessage id="app.table.column.productGroup" />,
            cell: supplier => {
                const names = (supplier.categories ?? [])
                    .map(category => getLocalizedString(locales, category.translations))
                    .filter(Boolean);

                return names.length > 0 ? (
                    <Tooltip title={names.join(', ')}>
                        <Box component="span">
                            {names.slice(0, 2).join(', ')}
                            {names.length > 2 && (
                                <Box component="span" sx={{ color: tokens.color.ink3 }}>
                                    {` +${names.length - 2}`}
                                </Box>
                            )}
                        </Box>
                    </Tooltip>
                ) : null;
            },
        },
        {
            key: 'responsiblePerson',
            sortKey: 'responsiblePerson',
            type: 'name',
            optional: true,
            head: <FormattedMessage id="app.table.column.responsiblePerson" />,
            quickFilter: {
                key: 'responsiblePersonIds',
                options: staff.map(contact => ({ value: String(contact.id), label: contact.fullName })),
                value: searchParams.get('responsiblePersonIds') ?? '',
                onChange: value => {
                    const next = new URLSearchParams(searchParams);

                    if (value) {
                        next.set('responsiblePersonIds', value);
                    } else {
                        next.delete('responsiblePersonIds');
                    }

                    next.set('page', '1');
                    setSearchParams(next);
                },
            },
            cell: supplier => supplier.responsiblePersonName,
        },
        {
            key: 'city',
            sortKey: 'city',
            type: 'name',
            head: <FormattedMessage id="app.table.column.cityAndCountry" />,
            // By country code, typed: the suppliers sit in eighteen countries,
            // and a list of all of them in a column this narrow helps nobody.
            // A list of the ones that actually occur needs a count per country
            // from the index, which is a piece of its own (#2194).
            quickFilter: {
                key: 'countries',
                placeholder: 'AT',
                value: searchParams.get('countries') ?? '',
                onChange: value => {
                    const next = new URLSearchParams(searchParams);

                    if (value) {
                        next.set('countries', value);
                    } else {
                        next.delete('countries');
                    }

                    next.set('page', '1');
                    setSearchParams(next);
                },
            },
            cell: supplier => {
                const address = addressOf(supplier);

                return [address?.city, address?.countryCode ?? address?.country].filter(Boolean).join(', ');
            },
        },
        {
            // A pill that says where the agreement stands and, where there is
            // one, the day it turns on. It used to read the date alone, so the
            // green pill of an open-ended agreement said "unbefristet" while
            // the legend called the same green "läuft" (#2212, R-26). Both now
            // use the words of the legend.
            key: 'contract',
            sortKey: 'contract',
            head: <FormattedMessage id="app.table.column.contract" />,
            cell: supplier => {
                const state = contractState(supplier);

                if (!state) {
                    return null;
                }

                // The day the state is measured against: where it begins for one
                // that has not started, where it ends for every other. An
                // open-ended agreement names none, which is what tells it apart
                // from one that merely runs for a while yet.
                const day = 'upcoming' === state ? supplier.classificationFrom : supplier.classificationTo;

                const label = (
                    <>
                        <FormattedMessage id={`app.contract.state.${state}`} />
                        {day && (
                            <>
                                {' · '}
                                <FormattedDate value={day} day="2-digit" month="2-digit" year="numeric" />
                            </>
                        )}
                    </>
                );

                return <Chip size="small" color={contractColor[state]} label={label} />;
            },
        },
    ];

    const headerActions = (
        <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(ROUTES.contactsAndOrganizations.suppliers.new)}
        >
            <FormattedMessage id="app.common.newSupplier" />
        </Button>
    );

    return (
        <PaloListView<SupplierListItem>
            filterChips={
                <PaloFilterChips
                    names={{
                        responsiblePersonIds: staff.map(contact => ({
                            value: String(contact.id),
                            label: contact.fullName,
                        })),
                    }}
                />
            }
            filterToggle={<PaloLockedSwitch checked={locked.includeLocked} onChange={locked.setIncludeLocked} />}
            emptyHint={
                lockedMatches > 0 ? (
                    <Box
                        component="span"
                        role="button"
                        onClick={() => locked.setIncludeLocked(true)}
                        sx={{ color: tokens.color.accent, cursor: 'pointer', textDecoration: 'underline' }}
                    >
                        {paloLockedHint(lockedMatches)}
                    </Box>
                ) : undefined
            }
            selection={selection}
            selectionActions={selectionActions}
            title={<FormattedMessage id="app.common.suppliers" />}
            pageTitle={intl.formatMessage({ id: 'app.common.suppliers' })}
            sort={sort}
            onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
            searchValue={searchParams.get('search') ?? ''}
            onSearchChange={value => {
                const next = new URLSearchParams(searchParams);
                if (value) {
                    next.set('search', value);
                } else {
                    next.delete('search');
                }
                next.set('page', '1');
                setSearchParams(next, { replace: true });
            }}
            headerActions={headerActions}
            filters={
                <>
                    <PaloFilterPanel listKey="suppliers" />
                    {savedFiltersMenu}
                </>
            }
            columns={orderColumns(columns)}
            columnFilter={columnFilter}
            onMoveColumn={moveColumn}
            hiddenColumnKeys={hiddenColumnKeys}
            shownColumnKeys={shownColumnKeys}
            onToggleColumn={toggleColumn}
            items={paginated.items}
            getRowId={supplier => supplier.id}
            requesting={requesting}
            emptyLabel="Keine Lieferanten."
            rowHref={detailHref}
            pagination={{
                count: paginated.total,
                page,
                limit,
                onPageChange: newPage => updateParams({ page: String(newPage) }),
                onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
            }}
        >
            <Stack
                direction="row"
                gap={2}
                alignItems="center"
                flexWrap="wrap"
                sx={{ mt: 1.5, fontSize: 12, color: tokens.color.ink2 }}
            >
                <Box component="span" sx={{ fontWeight: 600 }}>
                    <FormattedMessage id="app.contract.legend" />
                </Box>
                {(['active', 'expiring', 'expired', 'upcoming'] as ContractState[]).map(state => (
                    <Stack key={state} direction="row" gap={0.75} alignItems="center">
                        <Chip
                            size="small"
                            color={contractColor[state]}
                            label={<FormattedMessage id={`app.contract.state.${state}`} />}
                        />
                        <Box component="span">
                            <FormattedMessage
                                id={`app.contract.state.${state}.hint`}
                                values={{ days: EXPIRING_WITHIN_DAYS }}
                            />
                        </Box>
                    </Stack>
                ))}
            </Stack>
        </PaloListView>
    );
};
